import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  ChangeEvent,
} from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import Grid from '@material-ui/core/Grid';
import { FiCamera } from 'react-icons/fi';

import api from '../../../../../../../../services/api';

import no_image from '../../../../../../../../assets/no_image.png';
import InputLogged from '../../../../../../../../components/InputLogged';
import Select from '../../../../../../../../components/Select';
import TextAreaLogged from '../../../../../../../../components/TextAreaLogged';

import getValidationErrors from '../../../../../../../../utils/getValidationErrors';
import resizeFile from '../../../../../../../../utils/resizeFile';
import { useToast } from '../../../../../../../../hooks/toast';
import { useAuth } from '../../../../../../../../hooks/auth';
import { useSimpleProduct } from '../../../../../../../../hooks/newSimpleProduct';
import { useRegisterAdded } from '../../../../../../../../hooks/registerAdded';
import newMateriaPrima from '../../../../../../../../services/newMateriaPrima';

import {
  TabWrapper,
  LabelWrapper,
  NewImg,
  ButtonAction,
} from '../../../../drawer_styles';
import { SubmitButtonWrapper } from '../../../../../styles';
import material_styles from '../../../../drawer_styles/material_styles';

interface OptionType {
  id: string;
  value: string;
  label: string;
}

interface DetalhesProps {
  id_grupo: number;
  descricao_grupo: string;
  tipo_produto: number;
}

const Detalhes: React.FC<DetalhesProps> = ({
  id_grupo,
  descricao_grupo,
  tipo_produto,
}) => {
  const [optionsGroup, setOptionsGroup] = useState<OptionType[]>([]);
  const [optionsUnidadeMedida, setOptionsUnidadeMedida] = useState<
    OptionType[]
  >([]);
  const [unidadeSelected, setUnidadeSelected] = useState<OptionType>(
    {} as OptionType,
  );
  const [productImg, setProductImg] = useState<File | string>('');
  const [btnDisable, setBtnDisable] = useState(false);

  const [selectedOptionGroup, setSelectedOptionGroup] = useState({
    id: id_grupo,
    value: id_grupo,
    label: descricao_grupo,
  });

  const formRef = useRef<FormHandles>(null);

  const { user } = useAuth();
  const { product, createProduct, setDrawerValue } = useSimpleProduct();
  const {
    handleSetHasProductUpdated,
    handleSetDrawerWasClosed,
  } = useRegisterAdded();
  const { addToast } = useToast();

  const classes = material_styles();

  const handleChangeSelectedGroup = useCallback((data: any) => {
    setSelectedOptionGroup(data);
  }, []);

  const handleChangeSelectedUnidadeMedia = useCallback((data: any) => {
    setUnidadeSelected(data);
  }, []);

  useEffect(() => {
    async function loadGroups(): Promise<void> {
      const response = await api.get(
        `/grupo/${tipo_produto === 2 ? '4' : '1'}`,
      );

      const optionItems: OptionType[] = [];

      response.data.map((grupo: any) => {
        const option = {
          id: grupo.id,
          value: grupo.id,
          label: grupo.descricao,
        };

        optionItems.push(option);
      });

      setOptionsGroup([...optionItems]);
    }

    async function loadUnidades(): Promise<void> {
      const response = await api.get('/unidade_medida');

      const optionItems: OptionType[] = [];

      response.data.map((unidade_medida: any) => {
        const option = {
          id: unidade_medida.id,
          value: unidade_medida.id,
          label: unidade_medida.sigla,
        };

        optionItems.push(option);

        if (
          product.unidade_medida_id &&
          product.unidade_medida_id == unidade_medida.id
        ) {
          setUnidadeSelected({
            id: unidade_medida.id,
            value: unidade_medida.id,
            label: unidade_medida.sigla,
          });
        } else if (
          !product.unidade_medida_id &&
          unidade_medida.sigla === 'UN'
        ) {
          setUnidadeSelected({
            id: unidade_medida.id,
            value: unidade_medida.id,
            label: unidade_medida.sigla,
          });
        }
      });

      setOptionsUnidadeMedida(optionItems);
    }

    if (product.imagem) {
      setProductImg(product.imagem);
    }

    setBtnDisable(false);
    loadGroups();
    loadUnidades();
  }, [product, tipo_produto]);

  const handleImage = useCallback(() => {
    if (productImg) {
      if (typeof productImg === 'string') {
        return `${process.env.REACT_APP_SERVER_URL}/files/${productImg}`;
      }

      return URL.createObjectURL(productImg);
    }

    return no_image;
  }, [productImg]);

  const handleProdutoImgChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = await resizeFile(e.target.files[0]);

        setProductImg(file);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setBtnDisable(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          grupo_id: Yup.number().min(0).required('O grupo é obrigatório'),
          descricao: Yup.string().required('O nome do produto é obrigatório'),
          observacao: Yup.string().max(
            500,
            'A descrição do produto deve ter no máximo 500 caracteres',
          ),
          preco: Yup.number()
            .positive('Informe um preço válido.')
            .required('O preço do produto é obrigatório')
            .typeError('O preço do produto é obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        if (data.gtin) {
          const hasGtinStored = await api.get(
            `/produto/${user.empresa_id}?gtin=${data.gtin}`,
          );

          if (hasGtinStored.data) {
            addToast({
              type: 'error',
              title:
                'Já existe um produto cadastrado com este código de barras.',
            });
            setBtnDisable(false);

            return;
          }
        } else if (data.codigo) {
          const hasCodigoStored = await api.get(
            `/produto/${user.empresa_id}?codigo=${data.codigo}`,
          );

          if (hasCodigoStored.data) {
            addToast({
              type: 'error',
              title: 'Já existe um produto cadastrado com este código.',
            });
            setBtnDisable(false);

            return;
          }
        }

        if (tipo_produto === 2) {
          await newMateriaPrima({
            empresa_id: user.empresa_id,
            grupo_id: data.grupo_id,
            descricao: data.descricao,
            codigo: data.codigo,
            gtin: data.gtin,
            observacao: data.observacao,
            preco_custo: data.preco,
            unidade_medida_id: Number(unidadeSelected.value),
            estoque_atual: data.estoque_atual ? data.estoque_atual : 0,
            estoque_minimo: data.estoque_minimo ? data.estoque_minimo : 0,
            imagem: productImg || product.imagem,
            tipo: tipo_produto,
          });

          addToast({
            type: 'success',
            title: 'Matéria prima criada com sucesso.',
          });

          handleSetHasProductUpdated(true);

          return;
        }

        createProduct({
          empresa_id: user.empresa_id,
          grupo_id: data.grupo_id,
          descricao: data.descricao,
          codigo: data.codigo,
          gtin: data.gtin,
          observacao: data.observacao,
          preco: data.preco,
          unidade_medida_id: Number(unidadeSelected.value),
          estoque_minimo: data.estoque_minimo ? data.estoque_minimo : 0,
          estoque_atual: data.estoque_atual ? data.estoque_atual : 0,
          imagem: productImg || product.imagem,
          tipo: tipo_produto,
        });

        setDrawerValue(1);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          addToast({
            type: 'error',
            title: 'Preencha os dados corretamente pra continuar',
          });
        } else {
          const { message } = err.response.data;

          addToast({
            type: 'error',
            title: message,
          });
        }

        setBtnDisable(false);
      }
    },
    [
      productImg,
      product.imagem,
      addToast,
      user,
      createProduct,
      tipo_produto,
      handleSetHasProductUpdated,
      unidadeSelected,
      setDrawerValue,
    ],
  );

  return (
    <TabWrapper>
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={{
          descricao: product.descricao,
          observacao: product.observacao,
          estoque_minimo: product.estoque_minimo,
          estoque_atual: product.estoque_atual,
          preco: product.preco,
          codigo: product.codigo,
          gtin: product.gtin,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={8} className={classes.noPaddingTop}>
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.noPaddingBot}>
                <LabelWrapper>
                  <span>Grupo:</span>
                </LabelWrapper>

                <Select
                  value={selectedOptionGroup}
                  name="grupo_id"
                  onChange={handleChangeSelectedGroup}
                  placeholder="Selecione o grupo"
                  options={optionsGroup}
                />
              </Grid>

              <Grid item xs={12} className={classes.noVerticalPadding}>
                <LabelWrapper>
                  <span>Nome do produto:</span>
                </LabelWrapper>

                <InputLogged
                  name="descricao"
                  placeholder="Nome do produto"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Descrição:</span>
                </LabelWrapper>
                <TextAreaLogged
                  name="observacao"
                  placeholder="Detalhes do produto e informações para o cliente."
                  rows={3}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={4} className={classes.noVerticalPadding}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <LabelWrapper>
                  <span>Imagem do Produto:</span>
                </LabelWrapper>
                <NewImg>
                  <img src={handleImage()} alt="Grupo" />

                  <label htmlFor="imagem">
                    <FiCamera size={20} />
                    <input
                      type="file"
                      id="imagem"
                      onChange={handleProdutoImgChange}
                    />
                  </label>
                </NewImg>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Estoque Mínimo:</span>
                </LabelWrapper>

                <InputLogged
                  name="estoque_minimo"
                  placeholder="Estoque Mínimo"
                  type="number"
                  step=".001"
                />
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Estoque Atual:</span>
                </LabelWrapper>

                <InputLogged
                  name="estoque_atual"
                  placeholder="Estoque Atual"
                  type="number"
                  step=".001"
                />
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                {tipo_produto === 2 ? (
                  <>
                    <LabelWrapper>
                      <span>Preço Custo:</span>
                    </LabelWrapper>

                    <InputLogged
                      name="preco"
                      placeholder="Preço Custo"
                      type="number"
                      step=".001"
                    />
                  </>
                ) : (
                  <>
                    <LabelWrapper>
                      <span>Preço:</span>
                    </LabelWrapper>

                    <InputLogged
                      name="preco"
                      placeholder="Preço"
                      type="number"
                      step=".001"
                    />
                  </>
                )}
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Unidade de Medida:</span>
                </LabelWrapper>
                <Select
                  value={unidadeSelected}
                  name="unidade_medida_id"
                  placeholder="Selecione"
                  options={optionsUnidadeMedida}
                  onChange={handleChangeSelectedUnidadeMedia}
                />
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Código PDV:</span>
                </LabelWrapper>
                <InputLogged name="codigo" placeholder="Código" />
              </Grid>

              <Grid item xs={6} className={classes.noPaddingTop}>
                <LabelWrapper>
                  <span>Código de barras:</span>
                </LabelWrapper>
                <InputLogged name="gtin" placeholder="Código de Barras" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.noPaddingBot}>
            <SubmitButtonWrapper>
              <ButtonAction
                type="button"
                className="cancel"
                onClick={() => handleSetDrawerWasClosed(true)}
              >
                Cancelar
              </ButtonAction>

              <ButtonAction
                type="submit"
                className="submit"
                id="submit_button"
                disabled={btnDisable}
              >
                {tipo_produto === 2 ? 'Salvar' : 'Continuar'}
              </ButtonAction>
            </SubmitButtonWrapper>
          </Grid>
        </Grid>
      </Form>
    </TabWrapper>
  );
};

export default Detalhes;
